import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { exists } from '@jack-henry/frontend-utils/functions';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { AccountsService } from '@treasury/domain/accounts/accounts.service';
import { CompanyAccount } from '@treasury/domain/company/company-account.entity';
import { TmBaseComponent } from '@treasury/presentation';
import { type SelectItemVm, type TmValueChanged } from '@treasury/presentation/components/forms';
import {
    CurrencyRangeType,
    type CurrencyRange,
} from '@treasury/presentation/components/tm-currency-range';
import { DateRangeType, type DateRange } from '@treasury/presentation/components/tm-date-range';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import { difference } from '@treasury/utils';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { AccountType, PaymentType } from '../data/account-research-transactions-filter-items';

import '@treasury/presentation/components/tm-bottom-sheet';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/tm-date-range';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/tm-currency-range';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-multi-select';
import '@treasury/presentation/components/tm-radio-group';
import '@treasury/presentation/components/tm-select';
import '@treasury/presentation/components/tm-text-field';

const defaultTransactionDateRange: DateRange = {
    rangeType: DateRangeType.Today,
};

const defaultAmountRange: CurrencyRange = {
    rangeType: CurrencyRangeType.AllAmounts,
};

export const tagName = 'account-research-transactions-filter-sheet';
@customElement(tagName)
export class AccountResearchTransactionsFilterSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public open = false;

    @property({ type: String })
    public initialAccountType?: AccountType;

    @InjectProperty()
    declare private readonly AccountsService: AccountsService;

    @state()
    private loading = false;

    @state()
    private transactionDateRange: DateRange = defaultTransactionDateRange;

    @state()
    private accountTypes: AccountType[] = ['Checking', 'Savings', 'Loan'];

    @state()
    private accountTypesSelected: AccountType[] = [...this.accountTypes];

    @state()
    private accounts: CompanyAccount[] = [];

    @state()
    private filteredAccounts: CompanyAccount[] = [];

    @state()
    private accountsSelected: CompanyAccount[] = [];

    @state()
    private referenceNumberOperator = 'Specific #';

    @state()
    private referenceNumberOperatorItems: SelectItemVm<string>[] = [
        { label: 'Specific #', value: 'Specific #' },
        { label: 'Range', value: 'Range' },
    ];

    @state()
    private referenceNumberFrom!: string;

    @state()
    private referenceNumberTo!: string;

    @state()
    private amountRange: CurrencyRange = defaultAmountRange;

    @state()
    private paymentTypes: PaymentType[] = [];

    @state()
    private paymentTypesSelected: PaymentType[] = [];

    @state()
    private paymentDirection = 'Both';

    @DeepReactive()
    private validState = {
        transactionDate: true,
        accountTypes: true,
        accounts: true,
    };

    protected async firstUpdated() {
        this.loading = true;
        this.accounts = await this.AccountsService.getAllAccounts();
        this.accountsSelected = [...this.accounts];
        this.filteredAccounts = [...this.accounts];
        this.paymentTypes = (await this.AccountsService.getTransactionCodeGroups()).map(
            group => group.type
        );
        this.paymentTypesSelected = [...this.paymentTypes];
        if (this.initialAccountType) {
            this.accountTypesSelected = this.accountTypes.filter(
                type => type === this.initialAccountType
            );
        }
        this.accountTypesSelected = this.accountTypes.filter(
            type => type === 'Checking' || type === 'Savings'
        );
        this.filterAccountItems();
        this.onApplyFilters();
        this.loading = false;
    }

    protected render() {
        return html`<tm-bottom-sheet
            horizontal
            .open=${this.open}
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            <div slot="header-center">Research Transactions</div>
            <p slot="header-right"></p>
            <tm-body>${this.renderForm()}</tm-body>
            <tm-footer
                .buttonConfig=${[
                    {
                        text: 'Search',
                        onClick: () => {
                            this.onApplyFilters();
                        },
                        disabled: Object.values(this.validState).some(isValid => !isValid),
                    },
                    {
                        importance: 'secondary',
                        text: 'Reset',
                        onClick: () => {
                            this.onResetForm();
                        },
                    },
                ] as ButtonConfig[]}
            ></tm-footer>
        </tm-bottom-sheet>`;
    }

    private onApplyFilters() {
        this.open = false;
        this.dispatchEvent(new CustomEvent('close'));
        this.dispatchEvent(
            new CustomEvent('applyFilters', {
                detail: this.toDto(),
            })
        );
    }

    private onResetForm() {
        this.transactionDateRange = defaultTransactionDateRange;
        this.accountTypesSelected = [...this.accountTypes];
        this.accountsSelected = [...this.accounts];
        this.amountRange = { ...defaultAmountRange };
        this.referenceNumberOperator = 'Specific #';
        this.referenceNumberFrom = '';
        this.referenceNumberTo = '';
        this.paymentTypesSelected = [...this.paymentTypes];
        this.paymentDirection = 'Both';
        this.requestUpdate();
    }

    private toDto() {
        return {
            transactionDateOperator: this.transactionDateRange.rangeType,
            transactionDateType: this.transactionDateRange.rangeType,
            transactionDateFrom: format(
                this.transactionDateRange.startDate || new Date(),
                'yyyy-MM-dd'
            ),
            transactionDateTo: this.transactionDateRange.endDate
                ? format(this.transactionDateRange.endDate || new Date(), 'yyyy-MM-dd')
                : format(this.transactionDateRange.startDate || new Date(), 'yyyy-MM-dd'),
            // eslint-disable-next-line no-restricted-syntax
            transactionId: null,
            referenceNumberOperator: this.referenceNumberOperator,
            referenceNumberFrom: this.referenceNumberFrom,
            referenceNumberTo: this.referenceNumberTo,
            amountOperator: this.amountRange.rangeType,
            amountFrom: this.amountRange.floorAmount
                ? this.amountRange.floorAmount
                : this.amountRange.specificAmount,
            amountTo: this.amountRange.ceilAmount,
            paymentDirection: this.paymentDirection,
            paymentTypes: this.accountTypesSelected.some(type => type === 'Loan')
                ? []
                : this.paymentTypesSelected,
            accounts: this.accountsSelected.map(item => item.toDto()),
            accountTypes: this.accountTypesSelected,
            maxRecordCount: 500,
        };
    }

    private renderForm() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        return html`<form>
            <tm-date-range
                label="Transaction Date"
                required
                .initial=${this.transactionDateRange}
                @value-changed=${(e: TmValueChanged<DateRange>) => {
                    const { value, isValid } = e.detail;
                    this.validState.transactionDate = value?.rangeType === 'Today' ? true : isValid;
                    if (value) {
                        this.transactionDateRange = value;
                    }
                }}
            ></tm-date-range>
            <tm-multi-select
                label="Account Type"
                .allowSelectAll=${false}
                .allSelectedText=${'All Account Types'}
                .items=${this.accountTypes}
                .initial=${this.accountTypesSelected}
                .mapItem=${mapTypeToVm}
                @value-changed=${(e: TmValueChanged<AccountType[]>) => {
                    const selected = e.detail.value || [];
                    const diff = difference(selected, this.accountTypesSelected);
                    const loanTypeSelected = diff.some(item => item === 'Loan');

                    // loan is mutually exclusive with checking/savings
                    this.accountTypesSelected = loanTypeSelected
                        ? ['Loan']
                        : selected.filter(item => item !== 'Loan');
                    this.filterAccountItems();
                }}
            ></tm-multi-select>
            <tm-multi-select
                label="Accounts"
                .allowSelectAll=${true}
                .allSelectedText=${'All Accounts'}
                .items=${this.filteredAccounts}
                .initial=${[...this.accountsSelected]}
                .mapItem=${mapAccountToVm}
                @value-changed=${(e: TmValueChanged<CompanyAccount[]>) => {
                    this.accountsSelected = e.detail.value || [];
                    this.validState.accounts = this.accountsSelected.length > 0;
                }}
            ></tm-multi-select>
            <!-- check number group -->
            <tm-select
                label="Check/Reference #"
                .initial=${this.referenceNumberOperator}
                .items=${this.referenceNumberOperatorItems}
                @value-changed=${(e: CustomEvent) => {
                    if (!e.detail.value) {
                        return;
                    }
                    this.referenceNumberOperator = e.detail.value;
                    this.referenceNumberFrom = '';
                    this.referenceNumberTo = '';
                }}
            ></tm-select>
            ${this.referenceNumberOperator === 'Specific #'
                ? html`<tm-text-field
                      .initial=${this.referenceNumberFrom}
                      @value-changed=${(e: CustomEvent) => {
                          this.referenceNumberFrom = e.detail.value;
                      }}
                  ></tm-text-field>`
                : html`<div class="flex items-center justify-center">
                      <tm-text-field
                          .initial=${this.referenceNumberFrom}
                          class="min-w-0 w-full"
                          @value-changed=${(e: CustomEvent) => {
                              this.referenceNumberFrom = e.detail.value;
                          }}
                      ></tm-text-field>
                      <div class="px-2">-</div>
                      <tm-text-field
                          .initial=${this.referenceNumberTo}
                          class="min-w-0 w-full"
                          @value-changed=${(e: CustomEvent) => {
                              this.referenceNumberTo = e.detail.value;
                          }}
                      ></tm-text-field>
                  </div>`}
            <tm-currency-range
                .initial=${this.amountRange}
                @value-changed=${(e: TmValueChanged<CurrencyRange>) => {
                    const { value } = e.detail;
                    if (!value) {
                        return;
                    }
                    this.amountRange = value;
                }}
            ></tm-currency-range>
            ${this.renderTransactionTypeSelect()}
            <tm-radio-group
                .initial=${this.paymentDirection}
                .options=${[
                    {
                        label: 'Both',
                        value: 'Both',
                    },
                    {
                        label: 'Credit',
                        value: 'Credit',
                    },
                    {
                        label: 'Debit',
                        value: 'Debit',
                    },
                ]}
                @value-changed=${(e: CustomEvent) => {
                    this.paymentDirection = e.detail.value;
                }}
            ></tm-radio-group>
        </form>`;
    }

    private renderTransactionTypeSelect() {
        if (this.accountTypesSelected.some(type => type === 'Loan')) {
            return nothing;
        }

        return html`<tm-multi-select
            label="Transaction Type"
            .allowSelectAll=${true}
            .allSelectedText=${'All Transaction Types'}
            .items=${this.paymentTypes}
            .initial=${[...this.paymentTypesSelected]}
            .mapItem=${mapTypeToVm}
            @value-changed=${(e: TmValueChanged<PaymentType[]>) => {
                this.paymentTypesSelected = e.detail.value || [];
            }}
        ></tm-multi-select>`;
    }

    /**
     * Filters the available and selected accounts based on the selected account types.
     */
    private filterAccountItems() {
        if (this.accountTypesSelected.length === 0) {
            this.accountsSelected = [];
            return;
        }

        this.filteredAccounts = this.accounts.filter(a =>
            this.accountTypesSelected.some(selected => selected === a.type)
        );
        this.accountsSelected = this.accounts.filter(
            a => exists(a.type) && this.accountTypesSelected.includes(a.type as AccountType)
        );
        this.validState.accounts = this.accountsSelected.length > 0;
    }
}

function mapTypeToVm(type: AccountType | PaymentType): SelectItemVm<string> {
    return {
        label: type,
        value: type,
    };
}

function mapAccountToVm(account: CompanyAccount): SelectItemVm<CompanyAccount> {
    return {
        label: account.accountDisplayLabel || account.name,
        value: account,
    };
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AccountResearchTransactionsFilterSheet;
    }
}
